import React, { useEffect, useMemo, useState } from 'react';
import { Megaphone, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { PageSkeleton } from '../DashboardLoader';
import {
  getAdminAnnouncements,
  createAdminAnnouncement,
  updateAdminAnnouncement,
  deleteAdminAnnouncement,
  formatAdminDate,
} from '../../../services/adminApi';

const AdminAnnouncements = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const [form, setForm] = useState({
    title: '',
    content: '',
    type: 'info',
    priority: 'normal',
    target_audience: 'all_restaurants',
    expires_at: '',
  });

  const canSubmit = useMemo(() => {
    return form.title.trim().length >= 3 && form.content.trim().length >= 5;
  }, [form.title, form.content]);

  const isAnnouncementActive = (ann) => {
    if (!ann?.expires_at) return true;
    const t = new Date(ann.expires_at).getTime();
    if (!Number.isFinite(t)) return true;
    return t > Date.now();
  };

  const getExpiryLabel = (expiresAt) => {
    if (!expiresAt) return 'Sans expiration';
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(ms)) return 'Expiration invalide';
    if (ms <= 0) return 'Expirée';
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    if (days <= 1) return 'Expire aujourd hui';
    return `Expire dans ${days} jours`;
  };

  const filteredAnnouncements = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = announcements.filter((ann) => {
      const byPriority = priorityFilter === 'all' ? true : (ann.priority || 'normal') === priorityFilter;
      if (!byPriority) return false;
      const isActive = isAnnouncementActive(ann);
      const byStatus = statusFilter === 'all' ? true : statusFilter === 'active' ? isActive : !isActive;
      if (!byStatus) return false;
      if (!q) return true;
      const haystack = `${ann.title || ''} ${ann.content || ''}`.toLowerCase();
      return haystack.includes(q);
    });
    const priorityWeight = (p) => ((p || 'normal') === 'high' ? 2 : 1);
    const getTime = (d) => {
      const t = new Date(d || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'priority_desc') return priorityWeight(b.priority) - priorityWeight(a.priority);
      if (sortBy === 'priority_asc') return priorityWeight(a.priority) - priorityWeight(b.priority);
      if (sortBy === 'date_asc') return getTime(a.created_at) - getTime(b.created_at);
      return getTime(b.created_at) - getTime(a.created_at);
    });
    return sorted;
  }, [announcements, searchTerm, priorityFilter, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredAnnouncements.length / pageSize));
  const pagedAnnouncements = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAnnouncements.slice(start, start + pageSize);
  }, [filteredAnnouncements, page]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminAnnouncements();
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Impossible de charger les annonces.');
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, priorityFilter, statusFilter, sortBy]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError('');
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        type: form.type,
        priority: form.priority,
        target_audience: form.target_audience,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (editingId) {
        await updateAdminAnnouncement(editingId, payload);
      } else {
        await createAdminAnnouncement(payload);
      }
      setForm((prev) => ({ ...prev, title: '', content: '', expires_at: '' }));
      setEditingId(null);
      await loadAnnouncements();
    } catch (_) {
      setError(editingId ? "Impossible de modifier l'annonce." : "Impossible de créer l'annonce.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      setError('');
      await deleteAdminAnnouncement(id);
      await loadAnnouncements();
    } catch (_) {
      setError("Impossible de supprimer l'annonce.");
    }
  };

  const onToggleActive = async (ann) => {
    try {
      setError('');
      const currentlyActive = isAnnouncementActive(ann);
      const nextExpiresAt = currentlyActive ? new Date().toISOString() : null;
      await updateAdminAnnouncement(ann.id, { expires_at: nextExpiresAt });
      await loadAnnouncements();
    } catch (_) {
      setError("Impossible de changer le statut de l'annonce.");
    }
  };

  const onEdit = (ann) => {
    setEditingId(ann.id);
    setForm({
      title: ann.title || '',
      content: ann.content || '',
      type: ann.type || 'info',
      priority: ann.priority || 'normal',
      target_audience: ann.target_audience || 'all_restaurants',
      expires_at: ann.expires_at ? new Date(ann.expires_at).toISOString().slice(0, 16) : '',
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setForm((prev) => ({ ...prev, title: '', content: '', expires_at: '' }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="h-8 w-8 text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">Annonces restaurateurs</h1>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <form onSubmit={onCreate} className="bg-white rounded-lg p-4 space-y-3">
        {editingId && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Mode modification (annonce #{editingId})
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            className="border border-gray-300 rounded-lg px-3 py-2"
            placeholder="Titre"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <select
            className="border border-gray-300 rounded-lg px-3 py-2"
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
          >
            <option value="normal">Priorité normale</option>
            <option value="high">Priorité haute</option>
          </select>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2"
            value={form.target_audience}
            onChange={(e) => setForm((f) => ({ ...f, target_audience: e.target.value }))}
          >
            <option value="all_restaurants">Tous les restaurateurs</option>
            <option value="specific_restaurants">Restaurants ciblés</option>
          </select>
        </div>
        <textarea
          className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[110px]"
          placeholder="Contenu de l'annonce"
          value={form.content}
          onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date d'expiration (optionnel)</label>
            <input
              type="datetime-local"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
            />
          </div>
          <div className="text-xs text-gray-500 flex items-end">
            Laissez vide pour une annonce sans date de fin.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white disabled:bg-gray-400"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {editingId ? "Enregistrer les modifications" : "Publier l'annonce"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={onCancelEdit}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-lg">
        <div className="p-4 border-b border-gray-100">
          <div className="font-semibold text-gray-900 mb-3">Historique</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Rechercher titre/contenu"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">Toutes priorités</option>
              <option value="normal">Priorité normale</option>
              <option value="high">Priorité haute</option>
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Desactivees</option>
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_desc">Tri: plus recent</option>
              <option value="date_asc">Tri: plus ancien</option>
              <option value="priority_desc">Tri: priorite haute d'abord</option>
              <option value="priority_asc">Tri: priorite normale d'abord</option>
            </select>
            <div className="text-sm text-gray-500 flex items-center">
              {filteredAnnouncements.length} annonce(s) affichée(s)
            </div>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#054625] mx-auto" />
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune annonce</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {pagedAnnouncements.map((ann) => (
              <li key={ann.id} className="p-4 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    <span>{ann.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isAnnouncementActive(ann) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {isAnnouncementActive(ann) ? 'active' : 'desactivee'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{ann.content}</div>
                  <div className="text-xs text-gray-400 mt-2">
                    {ann.priority} - {ann.target_audience} - {ann.created_at ? formatAdminDate(ann.created_at) : '-'}
                  </div>
                  <div className={`text-xs mt-1 ${isAnnouncementActive(ann) ? 'text-amber-700' : 'text-gray-500'}`}>
                    {getExpiryLabel(ann.expires_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleActive(ann)}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                    title={isAnnouncementActive(ann) ? 'Desactiver' : 'Activer'}
                  >
                    {isAnnouncementActive(ann) ? 'Desactiver' : 'Activer'}
                  </button>
                  <button
                    onClick={() => onEdit(ann)}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                    title="Modifier"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => onDelete(ann.id)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {filteredAnnouncements.length > pageSize && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
            >
              Precedent
            </button>
            <span className="text-sm text-gray-500">
              Page {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;
